import React, { useState, useRef, forwardRef, useEffect, useMemo } from 'react';
import { useOptions } from '../../../stateManagers/hooks/useOptions';
import { useOptionFiles } from '../../../stateManagers/hooks/useOptionFiles';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export interface FeeItem {
  desc: string;
  amount: number;
  isOptional?: boolean;
  isLate?: boolean;
  isExempted?: boolean;
}

export interface ReceiptData {
  schoolName: string;
  schoolEmail: string;
  schoolPhone: string;
  schoolAddress: string;
  schoolLogoUrl: string;
  studentName: string;
  studentClass: string;
  rollNo: string;
  academicYear: string;
  invoicePrefix: string;
  feeItems: FeeItem[];
  vat: string;
  payMethod: string;
  terms: string;
}

export interface FeeRecordItem {
  fee_type_id?: string | { $oid: string };
  fee_name: string;
  amount: number;
  is_optional: boolean;
  is_exempted: boolean;
}

export interface FeeRecord {
  student_id: number | string;
  student_name: string;
  class: string;
  section: string | null;
  roll_no: number;
  fee_record_id: string | number | null; // ✅ matches StudentFeePanel
  session: string;
  month_year: string;
  receipt_no: string | null;
  paid_date: string | null;
  total_amount: string | number | null;
  paid_amount: string | number | null;
  fees_status: 'paid' | 'unpaid' | 'partial' | 'partially_paid' | 'exempted';
  pay_mode: string | null;
  profile_picture_url?: string | null;
  // ── Full breakdown ──
  is_exempted?: boolean;
  exemption_reason?: string | null;
  fee_items?: FeeRecordItem[];
  optional_services?: string[];
  late_fee_applied?: number;
  late_fee_days?: number;
}

export interface FeeReceiptGeneratorProps {
  studentName?: string;
  studentClass?: string;
  rollNo?: string | number;
  session?: string;
  feeRecords?: FeeRecord[];
  preSelectedMonths?: string[];
  onClose?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Theme
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  blue1:  '#1e3a8a',
  blue2:  '#2855ae',
  blue3:  '#3b82f6',
  orange: '#C0510A',
  orng2:  '#d96b22',
  surf:   '#fafbfc',
  bdr:    '#e5eaf0',
  text:   '#1f2937',
  muted:  '#6b7280',
  ghost:  '#9ca3af',
  late:   '#b91c1c',
};
const GRAD = `linear-gradient(135deg, ${T.blue1} 0%, ${T.blue2} 55%, ${T.blue3} 100%)`;

// Shared font stack — explicit Arial keeps word spacing stable in print output.
const FONT_STACK = 'Arial, "Helvetica Neue", Helvetica, sans-serif';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const ALL_MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const M2N: Record<string,string> = {
  january:'01',february:'02',march:'03',april:'04',may:'05',june:'06',
  july:'07',august:'08',september:'09',october:'10',november:'11',december:'12',
};

const expandSessionYears = (session: string): { yearA: string; yearB: string } => {
  const parts = (session || '').split('-');
  const a = parts[0] || String(new Date().getFullYear());
  let b = parts[1] || a;
  if (b.length === 2) b = a.slice(0, 2) + b; // "26" → "2026"
  return { yearA: a, yearB: b };
};

/**
 * Match receipt month name (e.g. "May") to feeRecords[].month_year ("2025-05").
 * Prefer exact year from session; fall back to any record with that month number.
 */
const findRecord = (
  records: FeeRecord[],
  month: string,
  session: string
): FeeRecord | undefined => {
  const mm = M2N[month.toLowerCase().trim()];
  if (!mm || !records?.length) return undefined;

  const { yearA, yearB } = expandSessionYears(session);
  // Nov/Dec → start year; Jan–Oct → end year (typical Indian session spanning two calendar years)
  const monthNum = parseInt(mm, 10);
  const preferredYear = monthNum >= 11 ? yearA : yearB;
  const preferred = `${preferredYear}-${mm}`;

  const exact = records.find((r) => r.month_year === preferred);
  if (exact) return exact;

  // Fallback: same calendar month in either session year
  const alt = `${monthNum >= 11 ? yearB : yearA}-${mm}`;
  const altMatch = records.find((r) => r.month_year === alt);
  if (altMatch) return altMatch;

  // Last resort: any record ending with -MM
  return records.find((r) => typeof r.month_year === 'string' && r.month_year.endsWith(`-${mm}`));
};

/**
 * Build the full itemized breakdown for a receipt from the record's actual
 * fee_items (mandatory + optional + late fee), instead of collapsing
 * everything into a single "School fees" line. Falls back to a single line
 * only for legacy records that were saved before itemized breakdowns existed.
 */
const buildItems = (rec: FeeRecord): FeeItem[] => {
  const status = (rec.fees_status || '').toLowerCase();
  const hasItems = Array.isArray(rec.fee_items) && rec.fee_items.length > 0;

  if (status === 'exempted') {
    if (hasItems) {
      return rec.fee_items!.map((fi) => ({
        desc: fi.fee_name,
        amount: 0,
        isOptional: fi.is_optional,
        isLate: fi.fee_name === 'Late Fee',
        isExempted: true,
      }));
    }
    return [{ desc: 'Fee exempted', amount: 0, isExempted: true }];
  }

  if (hasItems) {
    return rec.fee_items!.map((fi) => ({
      desc: fi.fee_name,
      amount: fi.amount,
      isOptional: fi.is_optional,
      isLate: fi.fee_name === 'Late Fee',
    }));
  }

  // Legacy fallback: no itemized breakdown was stored for this record
  const total = parseFloat(String(rec.total_amount ?? 0)) || 0;
  const paid = parseFloat(String(rec.paid_amount ?? 0)) || 0;
  const amount = paid > 0 ? paid : total;
  const label =
    status === 'partial' || status === 'partially_paid'
      ? 'Partial fee payment'
      : 'School fees';
  return [{ desc: label, amount }];
};

const fmt = (n: number) =>
  `₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Printing
//
// Both "Print This" and "Print All" copy already-rendered receipt markup into
// a fresh window. Every receipt style is inline, so the copied HTML prints
// exactly as previewed. The copy happens synchronously inside the click, so
// the popup isn't blocked and nothing depends on timers in the (now
// background) original tab. Printing starts once the logo images have loaded.
// ─────────────────────────────────────────────────────────────────────────────
const PRINT_CSS =
  `*{box-sizing:border-box;margin:0;padding:0;font-family:${FONT_STACK};letter-spacing:0px;word-spacing:0px;` +
  `-webkit-print-color-adjust:exact;print-color-adjust:exact}` +
  `html,body{background:#fff}` +
  `@page{size:A4 portrait;margin:0}` +
  `.frg-page{page-break-after:always;break-after:page}` +
  `.frg-page:last-child{page-break-after:auto;break-after:auto}` +
  // A4 at 96dpi is ~1122.5px tall; a receipt at exactly 1123px can spill a
  // fraction onto a blank extra page, so keep each one just under that.
  `.frg-page > div{min-height:1120px !important}`;

function printHtml(bodyHtml: string, title: string) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Please allow pop-ups for this site to print receipts.');
    return;
  }

  w.document.open();
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>` +
    `<style>${PRINT_CSS}</style></head><body>${bodyHtml}</body></html>`
  );
  w.document.close();

  // Wait for images (school logo) so they actually appear on paper.
  // A broken logo is hidden instead of printing a broken-image icon.
  const images = Array.from(w.document.images);
  const imagesReady = Promise.all(
    images.map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>(res => {
            img.onload = () => res();
            img.onerror = () => { img.style.display = 'none'; res(); };
          })
    )
  );
  const timeout = new Promise<void>(res => setTimeout(res, 3000));

  Promise.race([imagesReady, timeout]).then(() => {
    w.focus();
    w.onafterprint = () => w.close();
    w.print();
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Portrait Receipt (794px × 1123px)
// ─────────────────────────────────────────────────────────────────────────────
interface ReceiptProps {
  data: ReceiptData;
  month: string;
  index: number;
  record?: FeeRecord;
}

const PortraitReceipt = forwardRef<HTMLDivElement, ReceiptProps>(({ data, month, index, record }, ref) => {
  const {
    schoolName, schoolEmail, schoolPhone, schoolAddress, schoolLogoUrl,
    studentName, studentClass, rollNo, academicYear, invoicePrefix,
    feeItems, vat, payMethod, terms,
  } = data;

  const subtotal  = feeItems.reduce((s, f) => s + f.amount, 0);
  const vatAmt    = (subtotal * (parseFloat(vat) || 0)) / 100;
  const total     = subtotal + vatAmt;
  const invNo     = record?.receipt_no ?? `${invoicePrefix}-${String(index + 1).padStart(3, '0')}`;
  const rawDate   = record?.paid_date ? new Date(record.paid_date) : new Date();
  const invDate   = fmtDate(rawDate.toISOString());
  const payMode   = record?.pay_mode ? record.pay_mode.toUpperCase() : payMethod;
  const isExempted = record?.fees_status === 'exempted';

  const statusClr =
    record?.fees_status === 'paid'
      ? '#059669'
      : record?.fees_status === 'partial' || record?.fees_status === 'partially_paid'
        ? '#d97706'
        : record?.fees_status === 'exempted'
          ? '#4f46e5'
          : '#dc2626';

  // Every text style carries an explicit font + 0px spacing so print output
  // matches the on-screen preview.
  const BASE_TEXT: React.CSSProperties = {
    fontFamily:    FONT_STACK,
    letterSpacing: '0px',
    wordSpacing:   '0px',
  };

  const tdLabel: React.CSSProperties = {
    ...BASE_TEXT,
    padding: '9px 14px',
    fontSize: 11.5,
    color: T.muted,
    fontWeight: 600,
    borderBottom: `1px solid ${T.bdr}`,
    whiteSpace: 'nowrap',
    width: '38%',
  };
  const tdValue: React.CSSProperties = {
    ...BASE_TEXT,
    padding: '9px 14px',
    fontSize: 11.5,
    color: T.text,
    fontWeight: 700,
    borderBottom: `1px solid ${T.bdr}`,
  };

  return (
    <div ref={ref} style={{
      ...BASE_TEXT,
      background: '#fff',
      color: '#111',
      width: '794px',
      minHeight: '1123px',
      display: 'block',
      position: 'relative',
      overflow: 'hidden',
      padding: 0,
    }}>

      {/* ── Gradient header ── */}
      <div style={{ background: GRAD, padding: '28px 40px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          {schoolLogoUrl ? (
            <img src={schoolLogoUrl} alt="logo" style={{
              width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
              border: '3px solid rgba(255,255,255,0.5)', flexShrink: 0,
              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div style={{
              ...BASE_TEXT,
              width: 80, height: 80, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,0.18)', border: '3px solid rgba(255,255,255,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 30, fontWeight: 900, color: '#fff',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }}>{schoolName.charAt(0).toUpperCase()}</div>
          )}
          <div>
            <div style={{ ...BASE_TEXT, fontSize: 23, fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>{schoolName}</div>
            {schoolAddress && (
              <div style={{ ...BASE_TEXT, fontSize: 10.5, color: 'rgba(255,255,255,0.65)', marginTop: 4, fontWeight: 600 }}>
                {schoolAddress}
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
              {schoolPhone && <span style={{ ...BASE_TEXT, fontSize: 10.5, color: 'rgba(255,255,255,0.72)', fontWeight: 600 }}>📞 {schoolPhone}</span>}
              {schoolEmail && <span style={{ ...BASE_TEXT, fontSize: 10.5, color: 'rgba(255,255,255,0.72)', fontWeight: 600 }}>✉ {schoolEmail}</span>}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ ...BASE_TEXT, fontSize: 30, fontWeight: 900, color: '#fff', textTransform: 'uppercase' }}>FEE RECEIPT</div>
          <div style={{
            marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.15)', borderRadius: 20,
            padding: '5px 16px', border: '1px solid rgba(255,255,255,0.28)',
          }}>
            <span style={{ ...BASE_TEXT, fontSize: 12, color: 'rgba(255,255,255,0.88)', fontWeight: 700 }}>{month} · {academicYear}</span>
          </div>
        </div>
      </div>

      {/* Orange accent stripe */}
      <div style={{ height: 4, background: `linear-gradient(90deg,${T.orange},${T.orng2},${T.blue3})` }} />

      {/* ── Invoice meta + student info ── */}
      <div style={{ display: 'flex', gap: 0, background: '#f8fafc', borderBottom: `1px solid ${T.bdr}` }}>

        {/* Left: Invoice Details */}
        <div style={{ flex: 1, padding: '20px 24px 20px 40px', borderRight: `1px solid ${T.bdr}` }}>
          <div style={{ ...BASE_TEXT, fontSize: 10, fontWeight: 800, color: T.ghost, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
            Invoice Details
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.bdr}` }}>
            <tbody>
              {[
                ['Invoice No', invNo],
                ['Invoice Date', invDate],
                ['Academic Year', academicYear],
                ['Month', month],
              ].map(([k, v], i) => (
                <tr key={k}>
                  <td style={{ ...tdLabel, borderBottom: i === 3 ? 'none' : `1px solid ${T.bdr}`, background: '#f8fafc' }}>{k}</td>
                  <td style={{ ...tdValue, borderBottom: i === 3 ? 'none' : `1px solid ${T.bdr}` }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {record?.fees_status && (
            <div style={{
              ...BASE_TEXT,
              marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 12px', borderRadius: 20, fontSize: 10, fontWeight: 800,
              background: `${statusClr}18`, color: statusClr, border: `1px solid ${statusClr}40`,
              textTransform: 'uppercase',
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: statusClr, display: 'inline-block' }} />
              {record.fees_status}
            </div>
          )}
        </div>

        {/* Right: Billed To */}
        <div style={{ flex: 1, padding: '20px 40px 20px 24px' }}>
          <div style={{ ...BASE_TEXT, fontSize: 10, fontWeight: 800, color: T.ghost, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
            Billed To
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.bdr}` }}>
            <tbody>
              {[
                ['Student', studentName],
                ['Class', studentClass],
                ['Roll No', rollNo],
              ].map(([k, v], i) => (
                <tr key={k}>
                  <td style={{ ...tdLabel, borderBottom: i === 2 ? 'none' : `1px solid ${T.bdr}`, background: '#f8fafc' }}>{k}</td>
                  <td style={{ ...tdValue, borderBottom: i === 2 ? 'none' : `1px solid ${T.bdr}`, color: i === 0 ? T.blue1 : T.text, fontSize: i === 0 ? 13 : 11.5 }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Fee Items Table ── */}
      <div style={{ padding: '24px 40px 0' }}>
        <div style={{ ...BASE_TEXT, fontSize: 10, fontWeight: 800, color: T.ghost, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
          Fee Breakdown
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${T.bdr}`, borderRadius: 10, overflow: 'hidden' }}>
          <thead>
            <tr>
              {(['#', 'Description', 'Amount'] as const).map((h, i) => (
                <th key={h} style={{
                  ...BASE_TEXT,
                  background: T.blue1, color: '#fff',
                  padding: '11px 16px',
                  textAlign: i === 2 ? 'right' : i === 0 ? 'center' : 'left',
                  fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.6px',
                  width: i === 0 ? 52 : undefined,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {feeItems.map((item, i) => {
              const isCredit = item.amount < 0;
              const amountColor = item.isExempted
                ? T.ghost
                : item.isLate
                  ? T.late
                  : isCredit
                    ? '#059669'
                    : T.text;
              return (
                <tr key={i} style={{ background: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{
                    ...BASE_TEXT,
                    padding: '11px 16px', textAlign: 'center',
                    fontSize: 12, fontWeight: 700,
                    color: isCredit ? '#059669' : T.orange,
                    borderBottom: `1px solid ${T.bdr}`,
                  }}>
                    {isCredit ? '−' : String(i + 1).padStart(2, '0')}
                  </td>
                  <td style={{
                    ...BASE_TEXT,
                    padding: '11px 16px', fontSize: 13,
                    color: T.text, fontWeight: 500,
                    borderBottom: `1px solid ${T.bdr}`,
                  }}>
                    {item.desc}
                    {item.isOptional && (
                      <span style={{
                        ...BASE_TEXT,
                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                        color: T.blue1, background: 'rgba(59,130,246,0.12)',
                        padding: '2px 7px', borderRadius: 10,
                      }}>Optional</span>
                    )}
                    {item.isLate && (
                      <span style={{
                        ...BASE_TEXT,
                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                        color: T.late, background: 'rgba(185,28,28,0.1)',
                        padding: '2px 7px', borderRadius: 10,
                      }}>Late Fee</span>
                    )}
                    {item.isExempted && (
                      <span style={{
                        ...BASE_TEXT,
                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                        color: '#4f46e5', background: 'rgba(99,102,241,0.12)',
                        padding: '2px 7px', borderRadius: 10,
                      }}>Exempted</span>
                    )}
                    {isCredit && (
                      <span style={{
                        ...BASE_TEXT,
                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                        color: '#059669', background: '#d1fae5',
                        padding: '2px 7px', borderRadius: 10,
                      }}>Credit</span>
                    )}
                  </td>
                  <td style={{
                    ...BASE_TEXT,
                    padding: '11px 16px', fontSize: 13, textAlign: 'right',
                    fontWeight: 700,
                    color: amountColor,
                    borderBottom: `1px solid ${T.bdr}`,
                  }}>
                    {isCredit ? `− ${fmt(item.amount)}` : fmt(item.amount)}
                  </td>
                </tr>
              );
            })}

            {/* Subtotal */}
            <tr style={{ background: '#f8fafc' }}>
              <td colSpan={2} style={{
                ...BASE_TEXT,
                padding: '10px 16px', textAlign: 'right',
                fontSize: 12, color: T.muted, fontWeight: 600,
                borderBottom: `1px solid ${T.bdr}`,
              }}>Subtotal</td>
              <td style={{
                ...BASE_TEXT,
                padding: '10px 16px', textAlign: 'right',
                fontSize: 12, fontWeight: 700,
                color: T.text, borderBottom: `1px solid ${T.bdr}`,
              }}>{fmt(subtotal)}</td>
            </tr>

            {/* VAT */}
            <tr style={{ background: '#fff' }}>
              <td colSpan={2} style={{
                ...BASE_TEXT,
                padding: '10px 16px', textAlign: 'right',
                fontSize: 12, color: T.muted, fontWeight: 600,
                borderBottom: `1px solid ${T.bdr}`,
              }}>VAT ({vat || 0}%)</td>
              <td style={{
                ...BASE_TEXT,
                padding: '10px 16px', textAlign: 'right',
                fontSize: 12, fontWeight: 700,
                color: T.text, borderBottom: `1px solid ${T.bdr}`,
              }}>{fmt(vatAmt)}</td>
            </tr>

            {/* Total */}
            <tr style={{ background: T.blue1 }}>
              <td colSpan={2} style={{
                ...BASE_TEXT,
                padding: '13px 16px', textAlign: 'right',
                fontSize: 14, color: '#fff', fontWeight: 800,
              }}>TOTAL AMOUNT</td>
              <td style={{
                ...BASE_TEXT,
                padding: '13px 16px', textAlign: 'right',
                fontSize: 16, fontWeight: 900,
                color: '#fff',
              }}>{fmt(total)}</td>
            </tr>
          </tbody>
        </table>

        {record?.fees_status === 'partial' && (
          <div style={{ ...BASE_TEXT, marginTop: 8, fontSize: 11, color: '#d97706', fontWeight: 700, textAlign: 'right' }}>
            ⚠ Partial payment — balance pending
          </div>
        )}

        {isExempted && record?.exemption_reason && (
          <div style={{
            ...BASE_TEXT,
            marginTop: 10, padding: '10px 14px', borderRadius: 8,
            background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
            fontSize: 11.5, color: '#4f46e5', fontWeight: 600, lineHeight: 1.5,
          }}>
            <strong>Exemption reason:</strong> {record.exemption_reason}
          </div>
        )}
      </div>

      {/* ── Payment Details ── */}
      <div style={{ padding: '20px 40px 100px' }}>
        <div style={{ ...BASE_TEXT, fontSize: 10, fontWeight: 800, color: T.ghost, textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
          Payment Details
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${T.bdr}`, borderRadius: 10, overflow: 'hidden' }}>
          <tbody>
            {(
              [
                ['Payment Method', payMode],
                ['Paid On', fmtDate(record?.paid_date ?? null)],
                (record?.late_fee_applied ?? 0) > 0 && [
                  'Late Fee Applied',
                  `${fmt(record!.late_fee_applied!)}${record?.late_fee_days ? ` (${record.late_fee_days} day${record.late_fee_days === 1 ? '' : 's'} late)` : ''}`,
                ],
                ['Terms & Conditions', terms],
              ] as ([string, string] | false)[]
            )
              .filter(Boolean)
              .map((row, i, arr) => {
                const [k, v] = row as [string, string];
                const isLast = i === arr.length - 1;
                return (
                  <tr key={k} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                    <td style={{
                      ...tdLabel,
                      background: i % 2 === 0 ? '#f0f4f8' : '#f8fafc',
                      borderBottom: isLast ? 'none' : `1px solid ${T.bdr}`,
                      width: '30%',
                    }}>{k}</td>
                    <td style={{
                      ...tdValue,
                      borderBottom: isLast ? 'none' : `1px solid ${T.bdr}`,
                      fontSize: k === 'Terms & Conditions' ? 11 : 11.5,
                      color: k === 'Terms & Conditions' ? T.muted : k === 'Late Fee Applied' ? T.late : T.text,
                      fontWeight: k === 'Terms & Conditions' ? 400 : 700,
                      lineHeight: 1.7,
                    }}>{v}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* ── Footer ── */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: GRAD, padding: '12px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ ...BASE_TEXT, fontSize: 10.5, color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>
          {schoolName} · Fee Receipt
        </span>
        <span style={{ ...BASE_TEXT, fontSize: 10.5, color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>{invNo}</span>
      </div>
    </div>
  );
});
PortraitReceipt.displayName = 'PortraitReceipt';

// ─────────────────────────────────────────────────────────────────────────────
// PrintPage (kept as a public export)
// ─────────────────────────────────────────────────────────────────────────────
interface PrintPageProps {
  receipts: string[];
  data: ReceiptData;
  recordsByMonth: Record<string, FeeRecord | undefined>;
}
const PrintPage = forwardRef<HTMLDivElement, PrintPageProps>(({ receipts, data, recordsByMonth }, ref) => (
  <div ref={ref}>
    {receipts.map((month, i) => (
      <div key={month} style={{ pageBreakAfter: i < receipts.length - 1 ? 'always' : 'auto', marginBottom: i < receipts.length - 1 ? 40 : 0 }}>
        <PortraitReceipt data={data} month={month} index={i} record={recordsByMonth[month]} />
      </div>
    ))}
  </div>
));
PrintPage.displayName = 'PrintPage';

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
  @keyframes frg-in     { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes frg-bounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }

  .frg-overlay {
    position:fixed;inset:0;z-index:1100;
    background:rgba(15,23,42,0.62);backdrop-filter:blur(4px);
    display:flex;align-items:center;justify-content:center;
    animation:frg-in 0.22s ease;
  }
  .frg-drawer {
    background:#fff;border-radius:18px;
    width:96vw;max-width:1000px;max-height:96vh;
    display:flex;flex-direction:column;overflow:hidden;
    box-shadow:0 32px 80px rgba(15,23,42,0.35);
  }
  .frg-header {
    padding:16px 22px;flex-shrink:0;
    background:${GRAD};
    display:flex;align-items:center;justify-content:space-between;
    gap:12px;flex-wrap:wrap;
  }
  .frg-preview {
    flex:1;overflow:auto;padding:24px;background:#dde3ea;
    display:flex;flex-direction:column;align-items:center;gap:16px;
  }
  .frg-preview::-webkit-scrollbar{width:6px}
  .frg-preview::-webkit-scrollbar-thumb{background:rgba(30,58,138,0.18);border-radius:4px}

  .frg-action-btn{
    display:inline-flex;align-items:center;gap:6px;
    padding:8px 14px;border-radius:9px;font-size:12px;font-weight:700;
    cursor:pointer;border:none;font-family:inherit;
    transition:opacity 0.13s,transform 0.12s;white-space:nowrap;
  }
  .frg-action-btn:hover{opacity:0.88;transform:translateY(-1px)}
  .frg-action-btn:disabled{opacity:0.4;cursor:not-allowed;transform:none}

  .frg-nav-btn{
    padding:6px 14px;border-radius:8px;font-size:12px;font-weight:600;
    border:1px solid rgba(255,255,255,0.25);background:rgba(255,255,255,0.13);
    color:#fff;cursor:pointer;font-family:inherit;transition:background 0.13s;
  }
  .frg-nav-btn:hover:not(:disabled){background:rgba(255,255,255,0.22)}
  .frg-nav-btn:disabled{opacity:0.3;cursor:not-allowed}

  .frg-pill{
    display:inline-flex;align-items:center;gap:5px;
    font-size:11px;font-weight:600;color:rgba(255,255,255,0.72);
    background:rgba(255,255,255,0.12);padding:3px 9px;
    border-radius:20px;border:1px solid rgba(255,255,255,0.18);
    cursor:pointer;transition:background 0.13s;
  }
  .frg-pill.cur{background:rgba(255,255,255,0.26);color:#fff}
  .frg-pill:hover:not(.cur){background:rgba(255,255,255,0.2)}

  .frg-dot{
    display:inline-block;width:6px;height:6px;border-radius:50%;
    background:${T.blue3};margin:0 2px;
    animation:frg-bounce 1.2s infinite both;
  }
  .frg-dot:nth-child(1){animation-delay:-0.32s}
  .frg-dot:nth-child(2){animation-delay:-0.16s}
`;

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const FeeReceiptGenerator: React.FC<FeeReceiptGeneratorProps> = ({
  studentName: propStudentName = '',
  studentClass: propStudentClass = '',
  rollNo: propRollNo = '',
  session: propSession = '',
  feeRecords = [],
  preSelectedMonths = [],
  onClose,
}) => {
  const { options: schoolOpts, loading: optsLoading } = useOptions<string[]>([
    'school_email',
    'school_phone',
    'school_address',
  ]);

  // School name + logo — same source as TopBar
  const { optionFiles, loading: filesLoading } = useOptionFiles(['school_logo']);
  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);

  const schoolLogoUrl: string = optionFiles.school_logo?.files?.[0]?.url ?? '';
  const schoolName: string    = schoolDetails?.name ?? '';

  const schoolEmail   = schoolOpts['school_email']?.data?.[0]   ?? '';
  const schoolPhone   = schoolOpts['school_phone']?.data?.[0]   ?? '';
  const schoolAddress = schoolOpts['school_address']?.data?.[0] ?? '';

  const schoolLoading = optsLoading || filesLoading;

  const [form] = useState({
    studentName:   propStudentName  || 'Student Name',
    studentClass:  propStudentClass || 'Class X',
    rollNo:        String(propRollNo) || '001',
    academicYear:  propSession      || '',
    invoicePrefix: 'INV',
    feeItemsRaw:   'Monthly Tuition Fee | 0',
    vat:           '0',
    payMethod:     'Online Transfer',
    terms:         'Fees once paid are non-refundable.',
  });

  const [liveForm, setLiveForm] = useState(form);

  useEffect(() => {
    setLiveForm(f => ({
      ...f,
      studentName:  propStudentName  || f.studentName,
      studentClass: propStudentClass || f.studentClass,
      rollNo:       String(propRollNo) || f.rollNo,
      academicYear: propSession      || f.academicYear,
    }));
  }, [propStudentName, propStudentClass, propRollNo, propSession]);

  const recordsByMonth = useMemo(() => {
    const map: Record<string, FeeRecord | undefined> = {};
    ALL_MONTHS.forEach(m => { map[m] = findRecord(feeRecords, m, propSession); });
    return map;
  }, [feeRecords, propSession]);

  const [selectedMonths] = useState<string[]>(
    preSelectedMonths.length > 0 ? preSelectedMonths : ALL_MONTHS,
  );
  const [curIdx, setCurIdx] = useState(0);

  const receiptRef = useRef<HTMLDivElement>(null);
  // Hidden copy of every selected receipt — "Print All" copies its markup.
  const allRef = useRef<HTMLDivElement>(null);

  const buildData = (month: string) => {
    const rec = recordsByMonth[month];
    const feeItems = rec
      ? buildItems(rec)
      : liveForm.feeItemsRaw.split('\n').map(l => {
          const [d, a] = l.split('|').map(s => s.trim());
          return { desc: d || '', amount: parseFloat(a) || 0 };
        }).filter(f => f.desc);

    const data: ReceiptData = {
      schoolName:    schoolName || 'School',
      schoolEmail, schoolPhone, schoolAddress, schoolLogoUrl,
      studentName:   liveForm.studentName,
      studentClass:  liveForm.studentClass,
      rollNo:        liveForm.rollNo,
      academicYear:  liveForm.academicYear,
      invoicePrefix: liveForm.invoicePrefix,
      feeItems,
      vat:       liveForm.vat,
      payMethod: rec?.pay_mode ?? liveForm.payMethod,
      terms:     liveForm.terms,
    };
    return { data, record: rec };
  };

  const currentMonth  = selectedMonths[curIdx] ?? '';
  const currentRecord = recordsByMonth[currentMonth];
  const { data: curData } = buildData(currentMonth);

  const handlePrintThis = () => {
    if (!receiptRef.current) return;
    printHtml(`<div class="frg-page">${receiptRef.current.innerHTML}</div>`, 'Fee Receipt');
  };

  const handlePrintAll = () => {
    if (!allRef.current) return;
    printHtml(allRef.current.innerHTML, 'Fee Receipts');
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="frg-overlay" onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
        <div className="frg-drawer">

          {/* ══ Header ══ */}
          <div className="frg-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className="pi pi-file-pdf" style={{ fontSize: 16, color: '#fff' }} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>Fee Receipt</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {schoolLoading
                    ? <><span className="frg-dot" /><span className="frg-dot" /><span className="frg-dot" /></>
                    : <span>{schoolName || 'School'}</span>
                  }
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span>{selectedMonths.length} month{selectedMonths.length !== 1 ? 's' : ''}</span>
                  {feeRecords.length > 0 && (
                    <><span style={{ opacity: 0.4 }}>·</span>
                    <span style={{ color: 'rgba(52,211,153,0.85)' }}>{feeRecords.length} records</span></>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {selectedMonths.length > 0 && (
                <>
                  <button className="frg-action-btn" onClick={handlePrintAll} disabled={schoolLoading}
                    style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <i className="pi pi-print" style={{ fontSize: 11 }} /> Print All
                  </button>
                  <button className="frg-action-btn" onClick={handlePrintThis} disabled={schoolLoading}
                    style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <i className="pi pi-print" style={{ fontSize: 11 }} /> Print This
                  </button>
                </>
              )}
              <button onClick={onClose} style={{
                width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.1)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <i className="pi pi-times" style={{ fontSize: 12, color: '#fff' }} />
              </button>
            </div>
          </div>

          {/* ══ Preview ══ */}
          <div className="frg-preview">
            {selectedMonths.length === 0 ? (
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 12, color: T.ghost,
              }}>
                <div style={{
                  width: 72, height: 72, borderRadius: 18,
                  background: 'rgba(30,58,138,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="pi pi-calendar-times" style={{ fontSize: 28, color: T.blue3 }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>No months selected</span>
                <span style={{ fontSize: 12 }}>No fee records available</span>
              </div>
            ) : (
              <>
                {/* Nav bar */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: GRAD, borderRadius: 12, padding: '10px 16px', alignSelf: 'stretch',
                  boxShadow: '0 4px 14px rgba(30,58,138,0.2)',
                }}>
                  {selectedMonths.length > 1 ? (
                    <>
                      <button className="frg-nav-btn"
                        onClick={() => setCurIdx(i => Math.max(0, i - 1))}
                        disabled={curIdx === 0}>← Prev</button>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, flex: 1, justifyContent: 'center', margin: '0 10px' }}>
                        {selectedMonths.map((m, i) => (
                          <button key={m} onClick={() => setCurIdx(i)}
                            className={`frg-pill${i === curIdx ? ' cur' : ''}`}>
                            {m.slice(0, 3)}
                            {recordsByMonth[m] && (
                              <span style={{ width: 4, height: 4, borderRadius: '50%', flexShrink: 0, background: i === curIdx ? '#34d399' : '#059669' }} />
                            )}
                          </button>
                        ))}
                      </div>
                      <button className="frg-nav-btn"
                        onClick={() => setCurIdx(i => Math.min(selectedMonths.length - 1, i + 1))}
                        disabled={curIdx === selectedMonths.length - 1}>Next →</button>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <i className="pi pi-file" style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{currentMonth}</span>
                      {currentRecord && (
                        <span style={{
                          fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                          background: 'rgba(52,211,153,0.2)', color: '#34d399',
                          padding: '2px 8px', borderRadius: 20,
                        }}>{currentRecord.fees_status}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Receipt preview */}
                <div style={{
                  boxShadow: '0 12px 40px rgba(15,23,42,0.2)',
                  borderRadius: 6, overflow: 'hidden',
                  transform: 'scale(0.82)',
                  transformOrigin: 'top center',
                  marginBottom: '-100px',
                }}>
                  <div ref={receiptRef}>
                    <PortraitReceipt
                      data={curData}
                      month={currentMonth}
                      index={curIdx}
                      record={currentRecord}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hidden: every selected receipt, ready for "Print All".
          display:none doesn't matter — only the markup is copied. */}
      {selectedMonths.length > 0 && (
        <div ref={allRef} style={{ display: 'none' }} aria-hidden="true">
          {selectedMonths.map((m, i) => {
            const { data, record } = buildData(m);
            return (
              <div key={m} className="frg-page">
                <PortraitReceipt data={data} month={m} index={i} record={record} />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export { PortraitReceipt as Receipt, PrintPage };
export default FeeReceiptGenerator;